import {
  RECOMMENDATION_COLORS,
  RECOMMENDATION_LABELS,
} from "@components/campaigns/CandidateAssessmentSummary";
import { RadarChart } from "@components/charts/RadarChart";
import { ExportButton } from "@components/exports/ExportButton";
import { CopyButton } from "@components/shared/CopyButton";
import { type ThresholdFailure, ThresholdFlag } from "@components/shared/ThresholdFlag";
import { shortAnchorFor } from "@lib/constants/scoringScales";
import { EXPORT_CANDIDATE_ASSESSMENT_TO_GOOGLE_DOCS } from "@lib/graphql/mutations";
import type { ReactNode } from "react";
import { useMutation } from "urql";

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

interface BehaviouralAnchorData {
  id: string;
  evidenceText: string;
  sourceExcerpt: string | null;
  sourceArtefactType: string | null;
  sourceArtefactId: string | null;
  aiGenerated: boolean;
  createdAt: string;
}

interface CompetencyScoreData {
  id: string;
  score: number | null;
  anchorLabel?: string | null;
  evidence: string | null;
  notes: string | null;
  detailedNotes: string | null;
  capabilityDimension: {
    id: string;
    continuityId: string;
    name: string;
    description: string | null;
    idealScore: number | null;
  };
  behaviouralAnchors?: BehaviouralAnchorData[];
}

interface StructuralFitScoreData {
  id: string;
  briefFieldKey: string;
  briefFieldLabel: string;
  briefFieldValueExcerpt: string | null;
  score: number | null;
  anchorLabel?: string | null;
  evidence: string | null;
}

interface StructuralFitDimensionCoverageData {
  briefFieldKey: string;
  briefFieldLabel: string;
  coveringDimensions: Array<{ id: string; continuityId: string; name: string }>;
}

export interface AssessmentVersionData {
  overallScore: number | null;
  recommendation: string | null;
  overallSummary: string | null;
  strengths: string | null;
  concerns: string | null;
  culturalFitNotes: string | null;
  trajectoryNotes: string | null;
  riskNotes: string | null;
  versionNumber: number;
  effectiveFrom: string;
  assessor: { id: string; name: string };
  competencyScores: CompetencyScoreData[];
  structuralFitScores?: StructuralFitScoreData[];
  structuralFitOverallScore?: number | null;
  structuralFitDimensionCoverage?: StructuralFitDimensionCoverageData[];
  thresholdPassing?: boolean | null;
  thresholdOverriddenAt?: string | null;
  thresholdOverrideReason?: string | null;
  thresholdFailures?: ThresholdFailure[];
}

interface DimensionData {
  id: string;
  continuityId: string;
  name: string;
  idealScore: number | null;
}

interface AssessmentSummaryPanelProps {
  candidate: {
    fullName: string;
    currentRoleTitle: string | null;
    currentCompany: string | null;
    location: string | null;
  };
  assessment: AssessmentVersionData;
  dimensions: DimensionData[];
  // Optional ReactNode rendered next to the CopyButton in the panel header.
  // Used by campaign-context callers to mount the Google-Docs export button
  // without pulling urql into the panel's presentational footprint (so tests
  // that render the panel without a urql Provider keep working).
  headerActions?: ReactNode;
}

function scoreColor(score: number): string {
  if (score <= 1) return "text-error";
  if (score === 2) return "text-warning";
  return "text-success";
}

function assessmentCopyContent(
  candidate: AssessmentSummaryPanelProps["candidate"],
  assessment: AssessmentVersionData,
): { html: string; text: string } {
  const htmlParts: string[] = [];
  const textLines: string[] = [];

  htmlParts.push(`<h3>${escapeHtml(candidate.fullName)}</h3>`);
  textLines.push(candidate.fullName);

  const roleLine = [candidate.currentRoleTitle, candidate.currentCompany]
    .filter(Boolean)
    .join(" at ");
  if (roleLine) {
    htmlParts.push(`<p>${escapeHtml(roleLine)}</p>`);
    textLines.push(roleLine);
  }
  if (candidate.location) {
    htmlParts.push(`<p>${escapeHtml(candidate.location)}</p>`);
    textLines.push(candidate.location);
  }

  const assessedLine = `Assessed by ${assessment.assessor.name} · v${assessment.versionNumber} · ${new Date(
    assessment.effectiveFrom,
  ).toLocaleDateString()}`;
  htmlParts.push(`<p><em>${escapeHtml(assessedLine)}</em></p>`);
  textLines.push(assessedLine);

  htmlParts.push(`<h4>Overall Assessment</h4>`);
  textLines.push("", "Overall Assessment");
  if (assessment.overallScore != null) {
    htmlParts.push(`<p><strong>Score:</strong> ${assessment.overallScore}/5</p>`);
    textLines.push(`Score: ${assessment.overallScore}/5`);
  }
  if (assessment.recommendation) {
    const label = RECOMMENDATION_LABELS[assessment.recommendation] ?? assessment.recommendation;
    htmlParts.push(`<p><strong>Recommendation:</strong> ${escapeHtml(label)}</p>`);
    textLines.push(`Recommendation: ${label}`);
  }
  if (assessment.overallSummary) {
    htmlParts.push(`<p>${escapeHtml(assessment.overallSummary)}</p>`);
    textLines.push(assessment.overallSummary);
  }

  if (assessment.competencyScores.length > 0) {
    htmlParts.push(`<h4>Competency Scores</h4>`);
    textLines.push("", "Competency Scores");
    for (const cs of assessment.competencyScores) {
      if (cs.score == null) continue;
      const dim = cs.capabilityDimension;
      const requiredSuffix = dim.idealScore != null ? ` (required: ${dim.idealScore}/10)` : "";
      htmlParts.push(
        `<p><strong>${escapeHtml(dim.name)}:</strong> ${cs.score}/5${escapeHtml(requiredSuffix)}</p>`,
      );
      textLines.push(`${dim.name}: ${cs.score}/5${requiredSuffix}`);
      if (cs.evidence) {
        htmlParts.push(`<p><strong>Evidence:</strong> ${escapeHtml(cs.evidence)}</p>`);
        textLines.push(`Evidence: ${cs.evidence}`);
      }
      if (cs.notes) {
        htmlParts.push(`<p><strong>Notes:</strong> ${escapeHtml(cs.notes)}</p>`);
        textLines.push(`Notes: ${cs.notes}`);
      }
      if (cs.detailedNotes) {
        htmlParts.push(`<p><strong>Detailed notes:</strong> ${escapeHtml(cs.detailedNotes)}</p>`);
        textLines.push(`Detailed notes: ${cs.detailedNotes}`);
      }
    }
  }

  if (assessment.structuralFitScores && assessment.structuralFitScores.length > 0) {
    htmlParts.push(`<h4>Structural Fit</h4>`);
    textLines.push("", "Structural Fit");
    if (assessment.structuralFitOverallScore != null) {
      htmlParts.push(`<p><strong>Overall:</strong> ${assessment.structuralFitOverallScore}/5</p>`);
      textLines.push(`Overall: ${assessment.structuralFitOverallScore}/5`);
    }
    for (const sf of assessment.structuralFitScores) {
      if (sf.score == null) continue;
      htmlParts.push(`<p><strong>${escapeHtml(sf.briefFieldLabel)}:</strong> ${sf.score}/5</p>`);
      textLines.push(`${sf.briefFieldLabel}: ${sf.score}/5`);
      if (sf.evidence) {
        htmlParts.push(`<p>${escapeHtml(sf.evidence)}</p>`);
        textLines.push(sf.evidence);
      }
    }
  }

  const narrative: [string, string | null][] = [
    ["Strengths", assessment.strengths],
    ["Concerns", assessment.concerns],
    ["Cultural Fit", assessment.culturalFitNotes],
    ["Trajectory", assessment.trajectoryNotes],
    ["Risk Assessment", assessment.riskNotes],
  ];
  for (const [label, value] of narrative) {
    if (!value) continue;
    htmlParts.push(`<h4>${label}</h4><p>${escapeHtml(value)}</p>`);
    textLines.push("", label, value);
  }

  return { html: htmlParts.join(""), text: textLines.join("\n") };
}

// Pre-wired export button for the candidate's full assessment. Pass via the
// panel's `headerActions` prop. Owns the urql mutation so the panel itself
// remains presentational.
export function AssessmentExportButton({ campaignCandidateId }: { campaignCandidateId: string }) {
  const [, exportAssessment] = useMutation(EXPORT_CANDIDATE_ASSESSMENT_TO_GOOGLE_DOCS);

  const startAssessmentExport = async () => {
    const result = await exportAssessment({ input: { campaignCandidateId } });
    const payload = result.data?.exportCandidateAssessmentToGoogleDocs;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  return <ExportButton label="Export full assessment" onStart={startAssessmentExport} />;
}

export function AssessmentSummaryPanel({
  candidate,
  assessment,
  dimensions,
  headerActions,
}: AssessmentSummaryPanelProps) {
  const scoresMap: Record<string, number> = {};
  for (const cs of assessment.competencyScores) {
    if (cs.score != null) scoresMap[cs.capabilityDimension.continuityId] = cs.score;
  }

  const narrativeSections = [
    { label: "Strengths", value: assessment.strengths },
    { label: "Concerns", value: assessment.concerns },
    { label: "Cultural Fit", value: assessment.culturalFitNotes },
    { label: "Trajectory", value: assessment.trajectoryNotes },
    { label: "Risk Assessment", value: assessment.riskNotes },
  ].filter((s) => s.value);

  return (
    <div>
      <div className="mb-3 flex items-center justify-end gap-2">
        {headerActions}
        <CopyButton
          ariaLabel="Copy assessment"
          withLabel
          size={12}
          getContent={() => assessmentCopyContent(candidate, assessment)}
        />
      </div>
      <div className="space-y-6">
        {/* Candidate Header */}
        <div>
          <h3 className="text-lg font-semibold text-text-primary">{candidate.fullName}</h3>
          {(candidate.currentRoleTitle || candidate.currentCompany) && (
            <p className="text-sm text-text-muted">
              {[candidate.currentRoleTitle, candidate.currentCompany].filter(Boolean).join(" at ")}
            </p>
          )}
          {candidate.location && <p className="text-xs text-text-faint">{candidate.location}</p>}
          <p className="mt-1 text-xs text-text-faint">
            Assessed by {assessment.assessor.name} &middot; v{assessment.versionNumber} &middot;{" "}
            {new Date(assessment.effectiveFrom).toLocaleDateString()}
          </p>
        </div>

        {/* Overall Assessment */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            Overall Assessment
          </h4>
          <div className="flex items-center gap-3">
            {assessment.overallScore != null && (
              <span className="rounded bg-card-hover px-2.5 py-1 text-sm font-bold tabular-nums text-text-primary">
                {assessment.overallScore}/5
              </span>
            )}
            {assessment.recommendation && (
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${RECOMMENDATION_COLORS[assessment.recommendation] ?? ""}`}
              >
                {RECOMMENDATION_LABELS[assessment.recommendation]}
              </span>
            )}
            {(assessment.thresholdPassing != null || assessment.thresholdOverriddenAt) && (
              <ThresholdFlag
                passing={assessment.thresholdPassing ?? null}
                overridden={!!assessment.thresholdOverriddenAt}
                overrideReason={assessment.thresholdOverrideReason}
                failures={assessment.thresholdFailures}
              />
            )}
          </div>
          {assessment.overallSummary && (
            <p className="whitespace-pre-wrap text-sm text-text-secondary">
              {assessment.overallSummary}
            </p>
          )}
        </div>

        {/* Radar Chart */}
        {assessment.competencyScores.length > 0 && dimensions.length > 0 && (
          <div className="flex justify-center">
            <RadarChart
              dimensions={dimensions}
              candidateScores={scoresMap}
              candidateName={candidate.fullName}
              size="lg"
            />
          </div>
        )}

        {/* Per-Dimension Breakdown */}
        {assessment.competencyScores.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              Competency Scores
            </h4>
            {assessment.competencyScores.map((cs) => (
              <div key={cs.id} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-text-primary">
                      {cs.capabilityDimension.name}
                    </span>
                    {cs.score != null ? (
                      <span
                        className={`rounded bg-card-hover px-2 py-0.5 text-xs font-semibold tabular-nums ${scoreColor(cs.score)}`}
                      >
                        {cs.score}/5
                      </span>
                    ) : (
                      <span className="text-xs text-text-faint">unscored</span>
                    )}
                    {shortAnchorFor(cs.score) && (
                      <span className="text-xs italic text-text-faint">
                        {shortAnchorFor(cs.score)}
                      </span>
                    )}
                  </div>
                  {cs.capabilityDimension.idealScore != null && (
                    <span className="text-xs text-text-faint">
                      Required: {cs.capabilityDimension.idealScore}/10
                    </span>
                  )}
                </div>
                {cs.capabilityDimension.description && (
                  <p className="text-xs text-text-faint italic">
                    {cs.capabilityDimension.description}
                  </p>
                )}
                {cs.evidence && (
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                      Evidence
                    </span>
                    <p className="whitespace-pre-wrap text-xs text-text-secondary">{cs.evidence}</p>
                  </div>
                )}
                {cs.behaviouralAnchors && cs.behaviouralAnchors.length > 0 && (
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                      Behavioural anchors
                    </span>
                    <ul className="mt-1 space-y-1 text-xs text-text-secondary">
                      {cs.behaviouralAnchors.map((anchor) => (
                        <li key={anchor.id} className="rounded border border-border bg-card p-1.5">
                          <div className="mb-0.5 flex items-center gap-1.5">
                            {anchor.aiGenerated && (
                              <span className="rounded bg-info-bg px-1 py-0.5 text-[9px] font-medium uppercase tracking-wide text-info">
                                AI
                              </span>
                            )}
                            {anchor.sourceArtefactType && (
                              <span className="text-[10px] text-text-faint">
                                Source: {anchor.sourceArtefactType}
                              </span>
                            )}
                          </div>
                          <p className="whitespace-pre-wrap">{anchor.evidenceText}</p>
                          {anchor.sourceExcerpt && (
                            <p className="mt-0.5 italic text-text-faint">
                              "{anchor.sourceExcerpt}"
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {cs.notes && (
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                      Notes
                    </span>
                    <p className="whitespace-pre-wrap text-xs text-text-secondary">{cs.notes}</p>
                  </div>
                )}
                {cs.detailedNotes && (
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
                      Detailed Notes
                    </span>
                    <p className="whitespace-pre-wrap text-xs text-text-secondary">
                      {cs.detailedNotes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Structural Fit */}
        {((assessment.structuralFitScores && assessment.structuralFitScores.length > 0) ||
          (assessment.structuralFitDimensionCoverage &&
            assessment.structuralFitDimensionCoverage.length > 0)) && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Structural fit
              </h4>
              {assessment.structuralFitOverallScore != null && (
                <span className="text-xs text-text-faint">
                  Overall:{" "}
                  <span className="font-medium text-text-secondary">
                    {assessment.structuralFitOverallScore}/5
                  </span>
                </span>
              )}
            </div>
            <p className="text-xs text-text-faint">
              How populated brief fields are scored against this candidate.
            </p>
            {assessment.structuralFitScores?.map((sf) => (
              <div key={sf.id} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-sm font-medium text-text-primary"
                    title={`Brief field: ${sf.briefFieldKey}`}
                  >
                    {sf.briefFieldLabel}
                  </span>
                  {sf.score != null ? (
                    <span
                      className={`rounded bg-card-hover px-2 py-0.5 text-xs font-semibold tabular-nums ${scoreColor(sf.score)}`}
                    >
                      {sf.score}/5
                    </span>
                  ) : (
                    <span className="text-xs text-text-faint">unscored</span>
                  )}
                </div>
                {sf.evidence && (
                  <p className="whitespace-pre-wrap text-xs text-text-secondary">{sf.evidence}</p>
                )}
                {sf.briefFieldValueExcerpt && (
                  <details className="text-[11px] text-text-faint">
                    <summary className="cursor-pointer select-none">Brief excerpt</summary>
                    <p className="mt-1 whitespace-pre-wrap">{sf.briefFieldValueExcerpt}</p>
                  </details>
                )}
              </div>
            ))}
            {assessment.structuralFitDimensionCoverage?.map((cov) => (
              <div
                key={cov.briefFieldKey}
                className="rounded-lg border border-border/60 bg-card-hover/40 px-3 py-2"
              >
                <p className="text-xs text-text-secondary">
                  <span
                    className="font-medium text-text-primary"
                    title={`Brief field: ${cov.briefFieldKey}`}
                  >
                    {cov.briefFieldLabel}
                  </span>{" "}
                  — scored under{" "}
                  <span className="text-text-secondary">
                    {cov.coveringDimensions.map((d) => d.name).join(", ")}
                  </span>{" "}
                  in Competency Scores.
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Narrative Sections */}
        {narrativeSections.length > 0 && (
          <div className="space-y-4">
            {narrativeSections.map((section) => (
              <div key={section.label}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {section.label}
                </h4>
                <p className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">
                  {section.value}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
